'use client';

import { useMemo } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { ssdFilterGroups } from '@/lib/ssd-filter-config';

const PARAM_PREFIX = 'sf_';

export default function SsdFilters() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const activeFilters = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const group of ssdFilterGroups) {
      const raw = searchParams.get(`${PARAM_PREFIX}${group.id}`) || '';
      const values = raw
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean);
      map.set(group.id, new Set(values));
    }
    return map;
  }, [searchParams]);

  const toggleFilter = (groupId: string, value: string) => {
    const next = new URLSearchParams(searchParams.toString());
    const key = `${PARAM_PREFIX}${groupId}`;
    const current = new Set(
      (next.get(key) || '')
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean)
    );
    if (current.has(value)) {
      current.delete(value);
    } else {
      current.add(value);
    }
    if (current.size) {
      next.set(key, Array.from(current).join(','));
    } else {
      next.delete(key);
    }
    router.replace(`${pathname}?${next.toString()}`, { scroll: false });
  };

  const clearFilters = () => {
    const next = new URLSearchParams(searchParams.toString());
    ssdFilterGroups.forEach((group) => next.delete(`${PARAM_PREFIX}${group.id}`));
    router.replace(`${pathname}?${next.toString()}`, { scroll: false });
  };

  const hasFilters = Array.from(activeFilters.values()).some((set) => set.size > 0);

  return (
    <div className="mt-4 rounded-2xl border border-slate-200 bg-white/90 shadow-sm">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <h3 className="text-sm font-semibold text-slate-800">Bộ lọc SSD</h3>
        {hasFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            className="text-xs text-red-500 hover:underline"
          >
            Xóa bộ lọc
          </button>
        ) : null}
      </div>
      <div className="p-3 space-y-4">
        {ssdFilterGroups.map((group) => (
          <div key={group.id} className="space-y-2">
            <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
              {group.label}
            </div>
            <div className="space-y-1">
              {group.options.map((option) => {
                const selected = activeFilters.get(group.id)?.has(option.value);
                return (
                  <label
                    key={option.value}
                    className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={Boolean(selected)}
                      onChange={() => toggleFilter(group.id, option.value)}
                      className="h-3.5 w-3.5 rounded border-slate-300 text-primary focus:ring-primary/30"
                    />
                    <span>{option.label}</span>
                  </label>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
